import React, { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from "react-router-dom"
import { Grid, Box, CircularProgress } from "@mui/material"
import PageWrapper from "Pages/@PageWrapper"
import { dsColorNew } from "@palette"
import {
	AppCardTitle, AppCrudTable, AppToolbarCreateButton, AppAntSwitch, AppButtonIcon, AppResultsSkeleton, AppValueChip,
	AppStatusFilterToggle, HIDDEN_FILTER_CHIP,
} from "@components"
import { DS_REPRICING_ROUTES } from "@constants/routes"
import {
	dsRepricingDrawerData,
	dsRepricingListData,
	dsRepricingStatusFilter,
	dsRepricingIsLoading,
	dsRepricingIsSubmitting,
	dsRepricingOriginalData,
} from "Pages/DsRepricing/Page/@signals"
import DsRepricingDrawer from "Pages/DsRepricing/Page/Drawer"
import { weekdaysLabel } from "Pages/DsRepricing/Page/@components/Weekdays"
import { emptyPeriod, readPeriod } from "Pages/DsRepricing/Page/@components/periodShape"
import useDsRepricingTable from "Pages/DsRepricing/Page/@hooks/useTable"
import useDsRepricingFormSubmit from "Pages/DsRepricing/Page/@hooks/useFormSubmit"
import useDsRepricingActions from "Pages/DsRepricing/Page/@hooks/useActions"
import useDsRepricingStatus from "Pages/DsRepricing/Page/@hooks/useStatus"
import useDsRepricingEdit from "Pages/DsRepricing/Page/@hooks/useEdit"
import useDsRepricingClone from "Pages/DsRepricing/Page/@hooks/useClone"
import ConfirmEditModal from "Pages/DsRepricing/Page/@components/ConfirmEditModal"
import OverlapModal from "Pages/DsRepricing/Page/@components/OverlapModal"
import { DEFAULT_PRIORITY } from "Pages/DsRepricing/Page/@components/Priority"
import { AxisCatalogProvider, useAxisCatalog } from "Pages/DsRepricing/Setup/@services/axes"
import { SiteCatalogProvider, useSiteCatalog } from "Pages/DsRepricing/Setup/@services/sites"
import { faceLabel } from "Pages/DsRepricing/Setup/@services/faceLabel"
import { RateCardFacesProvider, useRateCardFaces } from "Pages/DsRepricing/Page/@services/rateCardFaces"
import { rateCardsThatFit } from "Pages/DsRepricing/Page/@services/rateCardsFit"

/**
 * Three states, one switch. The model has `draft | enabled | disabled`
 * (PRD 04) and the switch can only say two things, so the third is shown
 * next to it: a draft has never been live, a disabled one was. Painting
 * them the same would leave the operator unable to tell "I never turned
 * this on" from "I turned it off" (brief criterio 26).
 */
function StatusSwitch({ value, row }) {
	const { handleStatus } = useDsRepricingStatus(row.id, row.name)
	const isDraft = row.status === 'draft'
	return (
		<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.75 }}>
			<AppAntSwitch checked={value} onChange={() => handleStatus(!value)}/>
			{isDraft && (
				<AppValueChip tone="accent"
				              title="Never activated. Turn it on to let the engine run it."
				              sx={{ py: 0.25, fontSize: 11, fontWeight: 600 }}>
					Draft
				</AppValueChip>
			)}
		</Box>
	)
}

function ActionsCell({ row, onDelete }) {
	const { onEdit, isLoading: isEditLoading } = useDsRepricingEdit(row.id)
	const { onClone, isLoading: isCloneLoading } = useDsRepricingClone(row.id)
	return (
		<Box sx={{ display: 'flex', justifyContent: 'center' }}>
			{isEditLoading
				? <CircularProgress size={17} sx={{ m: 0.2 }}/>
				: <AppButtonIcon icon="pen" tooltip="Edit" onClick={e => { e.stopPropagation(); onEdit() }}/>
			}
			{isCloneLoading
				? <CircularProgress size={17} sx={{ m: 0.2 }}/>
				: <AppButtonIcon icon="clone" tooltip="Clone" onClick={e => { e.stopPropagation(); onClone() }}/>
			}
			<AppButtonIcon
				icon="trash-can"
				tooltip="Delete"
				onClick={e => { e.stopPropagation(); onDelete(row.id, row.name) }}
			/>
		</Box>
	)
}

const centered = v => <Box sx={{ display: 'flex', justifyContent: 'center' }}>{v}</Box>

const STRATEGY_LABEL = { market_trend: 'Market Trend', ranking: 'Ranking' }

const strategyLabel = s => {
	if (!s?.type) return '-'
	const base = STRATEGY_LABEL[s.type] ?? s.type
	if (s.type === 'ranking') return `${base} · #${s.targetPosition}`
	const extras = [
		s.rankCap != null ? `cap #${s.rankCap}` : null,
		s.excludeOutliersPct != null ? `>${s.excludeOutliersPct}% out` : null,
	].filter(Boolean)
	return extras.length ? `${base} · ${extras.join(', ')}` : base
}

const strategyFilterOptions = data => ({
	names: [...new Set(data.map(row => STRATEGY_LABEL[row?.strategy?.type]).filter(Boolean))],
	logic: (value, filters) => {
		if (!filters?.length) return false
		return !filters.includes(STRATEGY_LABEL[value?.type])
	},
})

const tethersSummary = ids => {
	const n = Array.isArray(ids) ? ids.length : 0
	return n === 0 ? '-' : `${n} tether${n === 1 ? '' : 's'}`
}

const undercutLabel = v => v?.value != null ? `${v.value} ${v.type === 0 ? '€' : '%'}` : null

const undercutFilterOptions = data => ({
	names: [...new Set(data.map(row => undercutLabel(row?.undercut)).filter(Boolean))],
	logic: (value, filters) => {
		if (!filters?.length) return false
		return !filters.includes(undercutLabel(value))
	},
})

const rangeLabel = (r, fromKey, toKey) => `${r[fromKey]} → ${r[toKey]}`

// One column for the three period modes: a rule carries seasons, lead time windows or weekdays, never
// two, so whichever one is filled is the whole answer to "when does this apply". Before this the
// column showed only seasons, which left a lead time or weekday rule looking like it had no period.
const periodLabels = period => {
	const { seasons, leadTimes, weekdays } = readPeriod({ period })
	if (seasons.length > 0) return seasons.map(s => `${s.seasonFrom} → ${s.seasonTo}`)
	if (leadTimes.length > 0) return leadTimes.map(w => `Lead ${w.leadFrom} → ${w.leadTo} d`)
	if (weekdays.length > 0) return [weekdaysLabel(weekdays)]
	return []
}

const rangeFilterOptions = (data, field, fromKey, toKey) => ({
	names: [...new Set(data.flatMap(row => (row?.[field] || []).map(r => rangeLabel(r, fromKey, toKey))))],
	logic: (arr, filters) => {
		if (!filters?.length) return false
		if (!Array.isArray(arr) || arr.length === 0) return true
		return !arr.some(r => filters.includes(rangeLabel(r, fromKey, toKey)))
	},
})

// Filtering by period covers the three modes, because the dropdown is built from the same labels the
// cell shows: a season range, a lead time window or the list of weekdays.
const periodFilterOptions = data => ({
	names: [...new Set(data.flatMap(row => periodLabels(row?.period)))],
	logic: (period, filters) => {
		if (!filters?.length) return false
		const labels = periodLabels(period)
		if (labels.length === 0) return true
		return !labels.some(label => filters.includes(label))
	},
})

// The rate cards each strategy's scope fits (CU-869fh646w, UX D6),
// derived once per list so the cell and the column filter read the
// same names. `null` while the faces are loading or could not be read
// — "I do not know", which the cell paints `—` and never `none`.
// Each entry: `{ name, full }` — the elided name for the cell, the four
// axes for its `title`.
const withRateCards = (rows, { faces, loading, failed }, sites, axes) => rows.map(row => ({
	...row,
	rateCards: loading || failed ? null
		: rateCardsThatFit(faces, row).map(f => ({
			name: faceLabel(f, sites, axes, { elide: true }),
			full: faceLabel(f, sites, axes),
		})),
}))

const RATE_CARDS_NONE = 'none'
const NONE_TITLE = "No rate card fits this strategy's sites, tariff type and insurance. It will decide nothing."

// Every rate-card name plus `none`, so "which strategies reach Sunny
// Cars?" and "which decide nothing?" are one dropdown each.
const rateCardsFilterOptions = data => ({
	names: [...new Set(data.flatMap(row => (row?.rateCards || []).map(c => c.name))), RATE_CARDS_NONE],
	logic: (cards, filters) => {
		if (!filters?.length) return false
		if (cards == null) return true
		if (cards.length === 0) return !filters.includes(RATE_CARDS_NONE)
		return !cards.some(c => filters.includes(c.name))
	},
})

const rateCardsCell = cards => {
	if (cards == null) return '—'
	if (cards.length === 0) {
		return <Box component="span" title={NONE_TITLE} sx={{ color: dsColorNew.dangerRed, fontWeight: 700 }}>{RATE_CARDS_NONE}</Box>
	}
	// Stacked like Period and Durations: never a count, never "+N more".
	return <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>{cards.map((c, i) => <span key={i} title={c.full}>{c.name}</span>)}</Box>
}

/** The three catalogues the wizard and the table read — the axis names
 *  Rate cards uses, the site names, and the rate-card faces themselves
 *  — loaded once per page, not per open (CU-869fe6mf4, CU-869fh646w). */
export default function DsRepricingConfigurationPage() {
	return (
		<AxisCatalogProvider>
			<SiteCatalogProvider>
				<RateCardFacesProvider>
					<StrategiesPage/>
				</RateCardFacesProvider>
			</SiteCatalogProvider>
		</AxisCatalogProvider>
	)
}

function StrategiesPage() {
	const { getTableData } = useDsRepricingTable()
	const [overlapState, setOverlapState] = useState(null)
	const { onSubmit } = useDsRepricingFormSubmit({
		getTableData,
		// `onConfirm` is null for a blocking (same-priority) overlap: there is nothing to confirm
		// past, only the other rule to go fix. Otherwise it saves anyway, priority already decided.
		onOverlap: (conflicts, onConfirm) => setOverlapState({ conflicts, onConfirm }),
	})
	const { onDisable, onDelete } = useDsRepricingActions()
	const [confirmEdit, setConfirmEdit] = useState(null)
	const facesState = useRateCardFaces()
	const sites = useSiteCatalog()
	const axes = useAxisCatalog()
	const list = dsRepricingListData.value
	const data = useMemo(() => withRateCards(list, facesState, sites, axes), [list, facesState, sites, axes])

	// `?rule=<id>` opens that rule straight away: it is the link the overlap warning uses to send
	// the user to the conflicting rule in a new tab.
	const [searchParams] = useSearchParams()
	const requestedRuleId = searchParams.get('rule')
	const { onEdit: openRequestedRule } = useDsRepricingEdit(requestedRuleId)

	const handleSubmit = (data) => {
		if (data.id) {
			setConfirmEdit({ data, original: dsRepricingOriginalData.peek() })
		} else {
			onSubmit(data)
		}
	}

	// "Save anyway" only reaches here for a non-blocking overlap (priority already breaks the tie),
	// so the modal is dismissed and the save that was held back runs.
	const handleOverlapConfirm = () => {
		overlapState?.onConfirm?.()
		setOverlapState(null)
	}

	useEffect(() => {
		getTableData()
	}, [])

	useEffect(() => {
		if (requestedRuleId) openRequestedRule()
	}, [requestedRuleId])

	return <PageWrapper
		head={`DS Rentacar | ${DS_REPRICING_ROUTES.STRATEGIES_TITLE}`}
		title={DS_REPRICING_ROUTES.STRATEGIES_TITLE}
		subtitle={DS_REPRICING_ROUTES.STRATEGIES_SUBTITLE}
	>
		<DsRepricingDrawer onSubmit={handleSubmit} isLoading={dsRepricingIsSubmitting.value}/>
		{confirmEdit && (
			<ConfirmEditModal
				open
				original={confirmEdit.original}
				current={confirmEdit.data}
				isLoading={dsRepricingIsSubmitting.value}
				onClose={() => setConfirmEdit(null)}
				onConfirm={() => { onSubmit(confirmEdit.data); setConfirmEdit(null) }}
			/>
		)}
		{overlapState && (
			<OverlapModal
				open
				conflicts={overlapState.conflicts}
				onConfirm={overlapState.onConfirm ? handleOverlapConfirm : null}
				onClose={() => setOverlapState(null)}
			/>
		)}
		<Grid container spacing={2}>
			<Grid item xs={12}>
				{dsRepricingIsLoading.value
					? <AppResultsSkeleton/>
					: <AppCrudTable
						title={<AppCardTitle icon="arrow-trend-up" title={DS_REPRICING_ROUTES.STRATEGIES_TITLE}>
							<Box component="span" sx={{ ml: 0.75 }}>
								<AppStatusFilterToggle aria-label="Strategies shown" filter={dsRepricingStatusFilter.value || []}
								                       onChange={filter => dsRepricingStatusFilter.value = filter}/>
							</Box>
						</AppCardTitle>}
						data={data}
						columns={[
							{ name: 'name', label: 'Name', options: { filterType: 'textField', customBodyRowRender: v => centered(v) } },
							{ name: 'priority', label: 'Priority', options: { filter: false, customBodyRowRender: v => centered(v ?? DEFAULT_PRIORITY) } },
							{ name: 'strategy', label: 'Strategy', options: { filterType: 'dropdown', filterOptions: strategyFilterOptions(dsRepricingListData.value), customBodyRowRender: v => centered(strategyLabel(v)) } },
							{ name: 'period', label: 'Period', options: { sort: false, filterType: 'dropdown', filterOptions: periodFilterOptions(dsRepricingListData.value), customBodyRowRender: v => { const labels = periodLabels(v); return centered(labels.length > 0 ? <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>{labels.map((label, i) => <span key={i}>{label}</span>)}</Box> : '-') } } },
							{ name: 'durations', label: 'Durations', options: { filterType: 'dropdown', filterOptions: rangeFilterOptions(dsRepricingListData.value, 'durations', 'durationFrom', 'durationTo'), customBodyRowRender: v => centered(Array.isArray(v) && v.length > 0 ? <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>{v.map((d, i) => <span key={i}>{`${d.durationFrom} → ${d.durationTo}`}</span>)}</Box> : '-') } },
											{ name: 'rateCards', label: 'Rate cards', options: { sort: false, filterType: 'dropdown', filterOptions: rateCardsFilterOptions(data), customBodyRowRender: v => centered(rateCardsCell(v)) } },
											{ name: 'undercut', label: 'Undercut', options: { filterType: 'dropdown', filterOptions: undercutFilterOptions(dsRepricingListData.value), customBodyRowRender: v => centered(v?.value != null ? `${v.value} ${v.type === 0 ? '€' : '%'}` : '-') } },
							{ name: 'tethers', label: 'Tethers', options: { filter: false, customBodyRowRender: v => centered(tethersSummary(v)) } },
							/* Removed columns (moved to Setup):
							     • Max Mark-Up (%)   → Setup → Rate Cards (markup cap)
							     • Tethering summary → Setup → Tethering (the strategy
							       still surfaces the count of applied ones as `Tethers`)
							     • Min / Max (€/day) → Setup → Price Limits           */
							{
								name: 'enabled', label: 'Off/On',
								options: {
									display: 'excluded',
									filterType: 'checkbox',
									filterList: dsRepricingStatusFilter.value,
									customBodyRowRender: (v, row) =>
										row?.status === 'draft' ? 'Draft' : (v ? 'Enabled' : 'Disabled'),
								}
							},
							{
								name: 'enabled', label: 'Off/On',
								options: {
									filter: false,
									customBodyRowRender: (v, row) => <StatusSwitch value={v} row={row}/>,
								}
							},
							{ name: 'id', label: 'Actions', options: { filter: false, customBodyRowRender: (_, row) => <ActionsCell row={row} onDelete={onDelete}/> } },
						]}
						options={{
							customToolbar: () => <AppToolbarCreateButton
								label="New strategy"
								icon="file-plus"
								sx={{ textTransform: 'none' }}
								onClick={() => dsRepricingDrawerData.value = { name: null, priority: DEFAULT_PRIORITY, strategy: { type: 'market_trend', rankCap: null, excludeOutliersPct: null }, undercut: { value: 2.5, type: 1, direction: 0 }, competitorRule: null, tethers: [], advanced: null, period: emptyPeriod(), tools: null, filterGroupOfficesExclusion: false, filterGroupSitesExclusion: false, filterGroupWebSuppliersExclusion: false, filterGroupProvidersExclusion: false, filterGroupWebSuppliers: [], filterAcrissExclusion: false, filterCategoriesExclusion: false, compareByCategory: false, compareEveryTariffType: false, compareEveryInsurance: false }}
							/>,
							// the Off/On filter has its own selector next to the title, so it gets no chip
							setFilterChipProps: (_, columnName) => columnName === 'enabled' ? { className: HIDDEN_FILTER_CHIP } : {},
							onFilterChange: (changedColumn, filterList, _, dataIdx) => {
								if (changedColumn === 'enabled')
									dsRepricingStatusFilter.value = filterList[dataIdx]
							},
							// The Excel export pointed at the legacy `/ds/list/excel`, which
							// reads `DS_REPRICING` by `uid`. After the move to the canonical
							// tables it would hand the operator a spreadsheet that does not
							// match the list in front of them, so it is off rather than wrong
							// (brief CU-869f043c1, Duda 3 — reinstating it is a PO call).
							download: false,
						}}
						rowActions={row => [
							row.enabled && { label: 'Disable', icon: 'ban', onClick: () => onDisable(row.id, row.name) },
							{ label: 'Delete', icon: 'trash', onClick: () => onDelete(row.id, row.name) },
						].filter(Boolean)}
					/>
				}
			</Grid>
		</Grid>
	</PageWrapper>
}
